import { addYears } from "date-fns";
import { memo } from "react";
import { useFormContext, useWatch } from "react-hook-form";

import type { OrderFormData } from "../../model";

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Checkbox,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/common/components";
import { formatDatetimeLocal } from "@/common/lib";

export const TradesCard = memo(() => {
  const form = useFormContext<OrderFormData>();
  const [
    tradingStartDate,
    tradingEndDate,
    collectionEnd,
    retradingBegin,
    retradingEnd,
    isSecondStage,
  ] = useWatch({
    control: form.control,
    name: [
      "tradeBegin",
      "tradeEnd",
      "collectionEnd",
      "retradingBegin",
      "retradingEnd",
      "retrading",
    ],
  });

  const date = new Date();
  const minDateToday = formatDatetimeLocal(date); // "2025-12-30T00:00"
  const maxDateOneYearFromNow = formatDatetimeLocal(addYears(date, 1)); // Максимальная дата: год от сегодня
  const previousStageEnd = isSecondStage ? retradingEnd : collectionEnd;
  const minResultsDate = [minDateToday ?? "", tradingStartDate, previousStageEnd]
    .filter(Boolean)
    .sort()
    .at(-1);

  const validateSchedule = () =>
    form.trigger([
      "tradeBegin",
      "tradeEnd",
      "collectionEnd",
      "retradingBegin",
      "retradingEnd",
      "resultsDate",
      "deadlineAt",
      "deadlineTo",
    ]);

  const changeSessionEnd = (value: string) => {
    // Обе даты описывают один момент. Проверяем зависимости после обновления обеих.
    form.setValue("tradeEnd", value, { shouldDirty: true });
    form.setValue("resultsDate", value, { shouldDirty: true });
    void validateSchedule();
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Этапы сбора предложений</CardTitle>
      </CardHeader>
      <CardContent className="space-y-5 md:space-y-8">
        {/* Период проведения сессии */}
        <div className="space-y-4">
          <h3 className="text-Buttons-main text-lg font-medium leading-5 normal-case">
            Период проведения сессии
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
            <FormField
              control={form.control}
              name="tradeBegin"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Начало сессии (время начала первого этапа) *
                    <span className="font-light">
                      - дата не должна быть позднее сегодняшнего дня
                    </span>
                  </FormLabel>
                  <Input
                    type="datetime-local"
                    placeholder="Начало сессии"
                    value={field.value}
                    onChange={(e) => {
                      form.setValue("tradeBegin", e.target.value, { shouldDirty: true });
                      form.setValue("collectionBegin", e.target.value, {
                        shouldDirty: true,
                      });
                      void validateSchedule();
                    }}
                    className="block"
                    min={minDateToday ?? ""}
                    max={maxDateOneYearFromNow ?? ""}
                  />
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="tradeEnd"
              render={({ field }) => (
                <FormItem className="self-end">
                  <FormLabel>Окончание сессии (время завершения третьего этапа) *</FormLabel>
                  <Input
                    type="datetime-local"
                    placeholder="Окончание сессии"
                    value={field.value}
                    onChange={(e) => changeSessionEnd(e.target.value)}
                    min={minResultsDate}
                    max={maxDateOneYearFromNow ?? ""}
                    className="block"
                  />
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>

        {/* Первый этап: Сбор предложений */}
        <div className="space-y-4">
          <h3 className="text-Buttons-hover_2 leading-5 normal-case">
            Первый этап: Сбор предложений
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
            <FormField
              control={form.control}
              name="collectionBegin"
              render={({ field }) => (
                <FormItem className="self-end">
                  <FormLabel>С (начало сбора) *</FormLabel>
                  <Input
                    {...field}
                    type="datetime-local"
                    placeholder="Начало сбора предложений"
                    min={tradingStartDate}
                    max={tradingEndDate}
                    className="block"
                    disabled
                  />
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="collectionEnd"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>До (окончание сбора)</FormLabel>
                  <Input
                    value={field.value}
                    type="datetime-local"
                    placeholder="Окончание сбора предложений"
                    className="block"
                    onChange={(e) => {
                      const value = e.target.value;
                      form.setValue("collectionEnd", value, { shouldDirty: true });
                      form.setValue("retradingBegin", isSecondStage ? value : "", {
                        shouldDirty: true,
                      });
                      void validateSchedule();
                    }}
                    min={tradingStartDate}
                    max={tradingEndDate}
                  />
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>

        {/* Второй этап: Переторжка */}
        <div className="space-y-4">
          <h3 className="text-Buttons-hover_2 leading-5 normal-case">Второй этап: Переторжка</h3>
          <div className="flex gap-2">
            <FormField
              control={form.control}
              name="retrading"
              render={({ field }) => (
                <Checkbox
                  checked={!!field.value}
                  onCheckedChange={(checked: boolean) => {
                    form.setValue("retrading", checked, { shouldDirty: true });
                    if (!checked) {
                      form.setValue("retradingBegin", "", {
                        shouldDirty: true,
                      });
                      form.setValue("retradingEnd", "", {
                        shouldDirty: true,
                      });
                    } else {
                      const retradingBegin = form.getValues("collectionEnd");
                      form.setValue("retradingBegin", retradingBegin, {
                        shouldDirty: true,
                      });
                    }
                    void validateSchedule();
                  }}
                />
              )}
            />
            <div className="space-y-1 leading-none">
              <FormLabel>Переторжка</FormLabel>
            </div>
          </div>

          {isSecondStage && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
              <FormField
                control={form.control}
                name="retradingBegin"
                render={({ field }) => (
                  <FormItem className="justify-end">
                    <FormLabel>Начало переторжки</FormLabel>
                    <Input
                      type="datetime-local"
                      value={field.value}
                      onChange={(e) => {
                        const value = e.target.value;
                        field.onChange(value);
                      }}
                      placeholder="Начало переторжки"
                      min={collectionEnd}
                      max={tradingEndDate}
                      className="block"
                      disabled
                    />
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="retradingEnd"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Окончание переторжки</FormLabel>
                    <Input
                      {...field}
                      type="datetime-local"
                      placeholder="Окончание переторжки"
                      onChange={(e) => {
                        form.setValue("retradingEnd", e.target.value, { shouldDirty: true });
                        void validateSchedule();
                      }}
                      min={retradingBegin}
                      max={tradingEndDate}
                      className="block"
                    />
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          )}
        </div>

        {/* Третий этап: Подведение итогов */}
        <div className="space-y-4">
          <h3 className="text-Buttons-hover_2 leading-5 normal-case">
            Третий этап: подведение итогов
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
            <FormField
              control={form.control}
              name="resultsDate"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Подведение итогов</FormLabel>
                  <Input
                    {...field}
                    type="datetime-local"
                    placeholder="Окончание подведения итогов"
                    onChange={(e) => changeSessionEnd(e.target.value)}
                    min={minResultsDate}
                    max={maxDateOneYearFromNow ?? ""}
                    className="block"
                  />
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
});
